export interface Column<T> {
  readonly key: string;
  readonly header: string;
  readonly cell: (row: T) => HTMLElement | string;
  readonly headerClassName?: string;
  readonly cellClassName?: string;
  // この列のセルを td ではなく `th scope="row"`（行見出し）で描画する。マトリクス表
  // （行見出し × 列見出し）で「どの行の値か」を支援技術へ伝えるために使う。
  readonly isRowHeader?: boolean;
}

export interface DataTableProps<T> {
  readonly caption: string;
  readonly columns: readonly Column<T>[];
  readonly rows: readonly T[];
  readonly onRowClick?: (row: T) => void;
  readonly emptyMessage?: string;
  readonly rowTestId?: string;
}

export function createDataTable<T>(props: DataTableProps<T>): HTMLElement {
  const table = document.createElement("table");
  table.className = "w-full border-collapse text-sm";

  const caption = document.createElement("caption");
  caption.className = "sr-only";
  caption.textContent = props.caption;

  const thead = document.createElement("thead");
  const headRow = document.createElement("tr");
  for (const col of props.columns) {
    const th = document.createElement("th");
    th.scope = "col";
    th.className =
      "border-b border-slate-200 py-2 px-2 text-left font-medium text-slate-700 " + (col.headerClassName ?? "");
    th.textContent = col.header;
    headRow.append(th);
  }
  thead.append(headRow);

  const tbody = document.createElement("tbody");
  if (props.rows.length === 0) {
    const tr = document.createElement("tr");
    const td = document.createElement("td");
    td.colSpan = props.columns.length;
    td.className = "py-4 px-2 text-slate-500";
    td.textContent = props.emptyMessage ?? "データがありません。";
    tr.append(td);
    tbody.append(tr);
  } else {
    for (const row of props.rows) {
      const tr = document.createElement("tr");
      tr.className = "border-b border-slate-100";
      if (props.rowTestId) tr.dataset["testid"] = props.rowTestId;
      if (props.onRowClick) {
        tr.tabIndex = 0;
        tr.className += " cursor-pointer hover:bg-slate-50";
        const fire = () => props.onRowClick?.(row);
        tr.addEventListener("click", fire);
        tr.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            fire();
          }
        });
      }
      for (const col of props.columns) {
        const isRowHeader = col.isRowHeader === true;
        const td = document.createElement(isRowHeader ? "th" : "td");
        if (isRowHeader) td.scope = "row";
        td.className =
          "py-2 px-2 text-slate-800 align-top " +
          (isRowHeader ? "text-left font-medium " : "") +
          (col.cellClassName ?? "");
        const content = col.cell(row);
        if (typeof content === "string") td.textContent = content;
        else td.append(content);
        tr.append(td);
      }
      tbody.append(tr);
    }
  }

  table.append(caption, thead, tbody);
  return table;
}
